import { useState } from 'react';

import { Check, CircleAlert, Download, LoaderCircle, QrCode, RefreshCw } from 'lucide-react';

import { assetUrl } from '../api';
import { SCENARIOS, STAGES, STATUS, STATUS_LABELS } from '../constants';
import {
    formatPrice,
    isQueued,
    needsPayment,
    pageSlots,
    progressPercent,
    stageIndex,
} from '../helpers';

import styles from '../index.module.scss';

function chargeText(task) {
    const price = formatPrice(task.payment?.price);
    const type = task.charge?.type;
    if (type === 'CASH') return `已支付${price ? ` ${price}` : ''}，失败重试不再收费`;
    if (type === 'REDEEM') return '已使用兑换码，失败重试不再收费';
    if (needsPayment(task)) {
        return isQueued(task)
            ? '尚未支付：支付后，资料整理完成会自动开始生成'
            : `待支付${price ? ` ${price}` : ''}，支付后开始生成`;
    }
    if (task.payment?.paid) return '已支付，失败重试不再收费';
    return '生成失败可以免费重试，不会重复收费';
}

function PaymentPanel({ task, canEdit, onPay, onBackToEdit }) {
    const price = formatPrice(task.payment?.price);
    return (
        <div className={styles.payPanel} role="status">
            <div className={styles.payPanelBody}>
                <strong>待支付{price ? ` ${price}` : ''}</strong>
                <p>
                    生成选项已确认，支付完成后自动开始生成，通常需要 2–8
                    分钟。也可以使用答辩PPT兑换码或金额码。
                </p>
            </div>
            <div className={styles.payPanelActions}>
                {canEdit ? (
                    <button type="button" className={styles.textButton} onClick={onBackToEdit}>
                        返回修改
                    </button>
                ) : null}
                <button type="button" className={styles.primaryButton} onClick={onPay}>
                    <QrCode size={16} aria-hidden />
                    去支付
                </button>
            </div>
        </div>
    );
}

function QueuedPanel({ task, cancelling, onCancel, onPay }) {
    const { done = 0, total = 0 } = task.materialState || {};
    const unpaid = needsPayment(task);
    return (
        <div className={styles.progressPanel} role="status" aria-live="polite">
            <p className={styles.progressText}>
                <LoaderCircle className={styles.spin} size={14} aria-hidden />
                {total > 1
                    ? `正在整理资料：文档较长，已分成 ${total} 部分，完成 ${done} 部分`
                    : '正在整理资料：提炼章节要点，并摘录关键数字和原文出处'}
            </p>
            <p className={styles.progressReason}>
                {unpaid
                    ? '完成支付后，资料整理好会自动开始生成；整理失败不会开始生成。'
                    : '已支付，资料整理完成后会自动开始生成；整理失败不会开始生成。'}
                现在可以离开页面，之后在“我的 PPT”里查看。
            </p>
            <div className={styles.queuedActions}>
                {unpaid ? (
                    <button type="button" className={styles.primaryButton} onClick={onPay}>
                        <QrCode size={16} aria-hidden />
                        去支付
                    </button>
                ) : null}
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={cancelling}
                    onClick={onCancel}
                >
                    {cancelling ? '正在取消…' : '取消自动生成'}
                </button>
            </div>
        </div>
    );
}

function ProgressPanel({ task }) {
    const current = stageIndex(task);
    const percent = progressPercent(task);
    const progress = task.progress || {};
    const planned = progress.plannedPages || progress.requestedPages || 0;
    const counts = planned
        ? `计划 ${planned} 页 · 已撰写 ${progress.writtenPages || 0} 页 · 已排版 ${progress.renderedPages || 0} 页`
        : '正在阅读资料，规划页数与大纲…';

    return (
        <div className={styles.progressPanel} role="status" aria-live="polite">
            <ol className={styles.stageList}>
                {STAGES.map((stage, index) => {
                    const state = index < current ? 'done' : index === current ? 'active' : 'todo';
                    return (
                        <li key={stage.key} data-state={state}>
                            <span aria-hidden>
                                {state === 'done' ? <Check size={13} /> : index + 1}
                            </span>
                            <div>
                                {stage.label}
                                <small>{stage.hint}</small>
                            </div>
                        </li>
                    );
                })}
            </ol>
            <div
                className={styles.progressBar}
                role="progressbar"
                aria-label="生成进度"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
            >
                <span style={{ transform: `scaleX(${percent / 100})` }} />
            </div>
            <p className={styles.progressText}>
                <LoaderCircle className={styles.spin} size={14} aria-hidden />
                {counts}
            </p>
            {progress.pageCountReason ? (
                <p className={styles.progressReason}>页数安排：{progress.pageCountReason}</p>
            ) : null}
        </div>
    );
}

function StatusNotice({ task, retrying, onRetry, onBackToEdit }) {
    const message = task.error?.message;
    if (task.status === STATUS.AWAITING_PAYMENT && task.payment?.paid) {
        return (
            <div className={styles.notice} data-tone="success" role="status">
                <LoaderCircle className={styles.spin} size={16} aria-hidden />
                <p>已收到付款，正在开始生成…</p>
            </div>
        );
    }
    if (task.status === STATUS.FAILED) {
        return (
            <div className={styles.notice} data-tone="error" role="alert">
                <CircleAlert size={16} aria-hidden />
                <p>{message || '生成失败'}。重试会接着已保存的内容继续，不再收费。</p>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={retrying}
                    onClick={onRetry}
                >
                    <RefreshCw size={14} aria-hidden />
                    {retrying ? '正在重试…' : '免费重试'}
                </button>
            </div>
        );
    }
    if (task.status === STATUS.SUBMITTING && !message) {
        return (
            <div className={styles.notice} role="status">
                <LoaderCircle className={styles.spin} size={16} aria-hidden />
                <p>正在提交给 PPT 服务…</p>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={retrying}
                    onClick={onRetry}
                >
                    长时间没有变化？重新提交
                </button>
            </div>
        );
    }
    if (task.status === STATUS.SUBMITTING) {
        return (
            <div className={styles.notice} role="status">
                <CircleAlert size={16} aria-hidden />
                <p>
                    {message ||
                        '提交结果还未确认。重新提交会使用同一份请求，PPT 服务只会创建一次，不会重复收费。'}
                </p>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={retrying}
                    onClick={onRetry}
                >
                    <RefreshCw size={14} aria-hidden />
                    {retrying ? '正在提交…' : '重新提交'}
                </button>
            </div>
        );
    }
    if (task.status === STATUS.SUBMIT_FAILED) {
        return (
            <div className={styles.notice} data-tone="error" role="alert">
                <CircleAlert size={16} aria-hidden />
                <p>{message || '提交失败'}，可以重新提交，不会重复收费。</p>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={retrying}
                    onClick={onRetry}
                >
                    重新提交
                </button>
                <button type="button" className={styles.textButton} onClick={onBackToEdit}>
                    返回修改
                </button>
            </div>
        );
    }
    return null;
}

function Viewer({ page, pageNumber, generateNotes }) {
    return (
        <div className={styles.viewer}>
            <figure className={styles.viewerStage}>
                <img
                    src={assetUrl(page.imageUrl)}
                    alt={`第 ${pageNumber} 页：${page.title}`}
                    width={1280}
                    height={720}
                />
            </figure>
            <aside className={styles.viewerSide} aria-label="当前页信息">
                <h3>
                    第 {pageNumber} 页 · {page.title || '未命名页面'}
                </h3>
                <h4>演讲备注</h4>
                {page.speakerNotes ? (
                    <p className={styles.notes}>{page.speakerNotes}</p>
                ) : (
                    <p className={styles.notesEmpty}>
                        {generateNotes
                            ? '这一页没有备注。'
                            : '生成时未开启演讲备注；下次可在“演示设置”里打开。'}
                    </p>
                )}
            </aside>
        </div>
    );
}

export default function ResultStep({
    headingRef,
    task,
    canEdit,
    retrying,
    cancelling,
    downloading,
    onPay,
    onRetry,
    onRestart,
    onBackToEdit,
    onCancelAutoSubmit,
    onDownload,
}) {
    const [selectedId, setSelectedId] = useState(null);
    const slots = pageSlots(task);
    const pages = slots.filter((slot) => slot.page).map((slot) => slot.page);
    const completed = task.status === STATUS.COMPLETED;
    const generating = task.status === STATUS.GENERATING;
    const queued = isQueued(task);
    const awaitingPayment = !queued && needsPayment(task);
    const selected =
        pages.find((page) => page.pageId === selectedId && page.imageUrl) ||
        pages.find((page) => page.imageUrl) ||
        null;
    const selectedNumber = selected ? pages.indexOf(selected) + 1 : 0;
    const scenario = SCENARIOS.find((item) => item.value === task.options?.scenario);
    const meta = [
        task.options?.templateName,
        scenario?.label,
        task.progress?.plannedPages ? `${task.progress.plannedPages} 页` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <section className={styles.workspace} aria-labelledby="ppt-result-title">
            <div className={styles.panelHead}>
                <div>
                    <h2 id="ppt-result-title" ref={headingRef} tabIndex={-1}>
                        {task.title || task.fileName || '答辩PPT'}
                    </h2>
                    {meta ? <p>{meta}</p> : null}
                </div>
                <span className={styles.statusTag} data-status={task.status}>
                    {queued ? '等待资料整理' : STATUS_LABELS[task.status] || task.status}
                </span>
            </div>

            <StatusNotice
                task={task}
                retrying={retrying}
                onRetry={onRetry}
                onBackToEdit={onBackToEdit}
            />
            {awaitingPayment ? (
                <PaymentPanel
                    task={task}
                    canEdit={canEdit}
                    onPay={onPay}
                    onBackToEdit={onBackToEdit}
                />
            ) : null}
            {queued ? (
                <QueuedPanel
                    task={task}
                    cancelling={cancelling}
                    onCancel={onCancelAutoSubmit}
                    onPay={onPay}
                />
            ) : null}
            {generating ? <ProgressPanel task={task} /> : null}
            {task.progress?.syncError ? (
                <div className={styles.notice} role="status">
                    <p>{task.progress.syncError}，稍后会自动刷新。</p>
                </div>
            ) : null}
            {task.pageErrors?.length ? (
                <div className={styles.notice} data-tone="warn" role="status">
                    <CircleAlert size={16} aria-hidden />
                    <p>有 {task.pageErrors.length} 页排版未通过，重试时只会重新处理这些页面。</p>
                </div>
            ) : null}

            {completed && selected ? (
                <Viewer
                    page={selected}
                    pageNumber={selectedNumber}
                    generateNotes={task.options?.generateNotes}
                />
            ) : null}

            {slots.length ? (
                <ol className={styles.thumbGrid} aria-label="页面预览">
                    {slots.map((slot, index) => {
                        const page = slot.page;
                        if (page?.imageUrl) {
                            return (
                                <li key={slot.key}>
                                    <button
                                        type="button"
                                        className={styles.thumb}
                                        aria-label={`第 ${index + 1} 页：${page.title}`}
                                        aria-current={
                                            completed && selected?.pageId === page.pageId
                                                ? 'true'
                                                : undefined
                                        }
                                        disabled={!completed}
                                        onClick={() => setSelectedId(page.pageId)}
                                    >
                                        <img
                                            src={assetUrl(page.imageUrl)}
                                            alt=""
                                            width={320}
                                            height={180}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <span>{index + 1}</span>
                                    </button>
                                </li>
                            );
                        }
                        return (
                            <li key={slot.key}>
                                <div className={styles.thumbPending}>
                                    <span>{index + 1}</span>
                                    <small>{page ? '排版中' : '待撰写'}</small>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            ) : null}

            <div className={styles.foot}>
                {queued ? null : (
                    <button type="button" className={styles.secondaryButton} onClick={onRestart}>
                        <RefreshCw size={15} aria-hidden />
                        再做一份
                    </button>
                )}
                <p className={styles.footNote}>{chargeText(task)}</p>
                {completed && task.exportUrls ? (
                    <div className={styles.footActions}>
                        {task.exportUrls.pdf ? (
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                disabled={Boolean(downloading)}
                                onClick={() => onDownload('pdf')}
                            >
                                {downloading === 'pdf' ? (
                                    <LoaderCircle className={styles.spin} size={15} aria-hidden />
                                ) : (
                                    <Download size={15} aria-hidden />
                                )}
                                下载 PDF
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={Boolean(downloading) || !task.exportUrls.pptx}
                            onClick={() => onDownload('pptx')}
                        >
                            {downloading === 'pptx' ? (
                                <LoaderCircle className={styles.spin} size={16} aria-hidden />
                            ) : (
                                <Download size={16} aria-hidden />
                            )}
                            下载 PPTX（可编辑）
                        </button>
                    </div>
                ) : null}
            </div>
        </section>
    );
}
